import { z } from 'zod'
import { LocaleSchema } from './auth'

// The email outbox (intra.email_outbox) and its admin log. The sending
// machinery itself is server-only (apps/api/src/email); what lives here is
// the vocabulary the API and the admin app share.

// Why a member got an email, and what they may opt out of. `security` is
// never optional: a password reset or a "your booking was cancelled by
// someone else" must always arrive.
export const EMAIL_CATEGORIES = ['security', 'booking', 'reminder', 'expiry', 'fleet', 'school', 'admin'] as const
export type EmailCategory = (typeof EMAIL_CATEGORIES)[number]
export const EmailCategorySchema = z.enum(EMAIL_CATEGORIES)

// See db/flyway/schema/sql/V470__email_outbox.sql for what each means.
export const EMAIL_STATUSES = ['pending', 'sending', 'sent', 'failed', 'skipped', 'suppressed'] as const
export type EmailStatus = (typeof EMAIL_STATUSES)[number]
export const EmailStatusSchema = z.enum(EMAIL_STATUSES)

// One row of the admin email log. props is deliberately absent: it can
// carry anything a template needs, including a one-time link.
export interface EmailLogEntry {
  emailId: number
  template: string
  category: EmailCategory
  locale: 'fi' | 'sv' | 'en'
  userId: string | null
  userDisplayName: string | null
  toAddress: string
  status: EmailStatus
  attempts: number
  lastError: string | null
  subject: string | null
  transport: string | null
  sendAfter: string
  sentAt: string | null
  createdAt: string
}

export const EmailLogQuerySchema = z.object({
  status: EmailStatusSchema.optional(),
  template: z.string().trim().max(64).optional(),
  // Matches the address or the member's name.
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  before: z.coerce.number().int().positive().optional(),
})
export type EmailLogQuery = z.infer<typeof EmailLogQuerySchema>

// The template catalogue the admin preview page lists.
export interface EmailTemplateInfo {
  key: string
  category: EmailCategory
  description: string
}

export interface EmailPreview {
  subject: string
  html: string
  text: string
}

export const EmailPreviewQuerySchema = z.object({ locale: LocaleSchema.default('fi') })

// POST /api/admin/email/test: render a template's fixture and send it to
// the caller through the live transport.
export const SendTestEmailSchema = z.object({
  template: z.string().trim().min(1).max(64),
  locale: LocaleSchema,
})
export type SendTestEmailInput = z.infer<typeof SendTestEmailSchema>

// What a member may turn off from their profile. `security` is always on.
export const OPTIONAL_EMAIL_CATEGORIES = ['booking', 'reminder', 'expiry', 'fleet', 'school', 'admin'] as const
export type OptionalEmailCategory = (typeof OPTIONAL_EMAIL_CATEGORIES)[number]

export interface EmailPreference {
  category: EmailCategory
  enabled: boolean
  locked: boolean
}

// PUT /api/auth/me/email-preferences
export const UpdateEmailPreferenceSchema = z.object({
  category: z.enum(OPTIONAL_EMAIL_CATEGORIES),
  enabled: z.boolean(),
})
